import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import {
  filterCheckboxOptionClass,
  filterInputClass,
  filterLabelClass,
  selectDropdownOpenClass,
  selectDropdownOptionClass,
  selectDropdownPanelClass,
} from './uiClasses.js';

const parseOptionChildren = (children) =>
  React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => ({
      value: child.props.value ?? '',
      label: String(child.props.children ?? child.props.value ?? ''),
      disabled: Boolean(child.props.disabled),
    }));

const optionLabel = (opt) => String(opt?.label ?? '');

const valuesEqual = (a, b) => String(a) === String(b);

const isSelectedValue = (value, optValue, multiple) => {
  if (multiple) {
    return Array.isArray(value) && value.some((v) => valuesEqual(v, optValue));
  }
  return valuesEqual(value, optValue);
};

/**
 * List-filter combobox: search input on top, type to suggest, Enter to select & filter.
 * Drop-in for native <select> / filter dropdowns (synthetic onChange).
 * Set `multiple` for multi-select filters (payment mode, locations, etc.).
 */
const SearchableFilterSelect = ({
  value = '',
  onChange,
  name,
  id,
  options,
  children,
  label,
  disabled = false,
  placeholder,
  emptyLabel,
  className = '',
  labelClassName = filterLabelClass,
  multiple = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const generatedId = useId();
  const fieldId = id || name || generatedId;

  const parsedOptions = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) return options;
    return parseOptionChildren(children);
  }, [options, children]);

  const selectedValues = multiple
    ? (Array.isArray(value) ? value : value !== '' && value != null ? [value] : [])
    : [];

  const selectedOption = !multiple
    ? parsedOptions.find((opt) => valuesEqual(opt.value, value))
    : null;

  const selectedLabels = multiple
    ? parsedOptions
        .filter((opt) => selectedValues.some((v) => valuesEqual(v, opt.value)))
        .map(optionLabel)
    : [];

  const allLabel =
    emptyLabel ||
    placeholder ||
    optionLabel(parsedOptions.find((opt) => String(opt.value) === '')) ||
    (multiple ? 'All' : 'All');

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = multiple
      ? parsedOptions.filter((opt) => String(opt.value) !== '')
      : parsedOptions;
    if (!q) return list;
    return list.filter((opt) => optionLabel(opt).toLowerCase().includes(q));
  }, [parsedOptions, query, multiple]);

  const listOptions = useMemo(() => {
    if (!multiple) return filteredOptions;
    const q = query.trim().toLowerCase();
    const allOpt = { value: '', label: allLabel, isAll: true };
    const showAll = !q || allLabel.toLowerCase().includes(q);
    return showAll ? [allOpt, ...filteredOptions] : filteredOptions;
  }, [multiple, filteredOptions, query, allLabel]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery('');
    }
  }, [disabled]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-option-index="${highlight}"]`);
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, open]);

  const emitChange = (newValue) => {
    onChange?.({ target: { name, value: newValue } });
  };

  const selectOption = (opt) => {
    if (disabled || opt?.disabled) return;

    if (multiple && opt?.isAll) {
      emitChange([]);
      setQuery('');
      return;
    }

    if (multiple) {
      const exists = selectedValues.some((v) => valuesEqual(v, opt.value));
      const next = exists
        ? selectedValues.filter((v) => !valuesEqual(v, opt.value))
        : [...selectedValues, opt.value];
      emitChange(next);
      setQuery('');
      return;
    }

    emitChange(opt.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    emitChange(multiple ? [] : '');
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      setHighlight((prev) => Math.min(prev + 1, Math.max(listOptions.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      setHighlight((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      if (!multiple) {
        const q = query.trim();
        if (!q && filteredOptions.some((opt) => String(opt.value) === '')) {
          selectOption(filteredOptions.find((opt) => String(opt.value) === '') || { value: '' });
          return;
        }
      }
      const choice = listOptions[highlight] || listOptions[0];
      if (choice) selectOption(choice);
    }
  };

  const hasSelection = multiple
    ? selectedValues.length > 0
    : value !== '' && value != null;

  const displayValue = open
    ? query
    : multiple
      ? (selectedLabels.length ? selectedLabels.join(', ') : allLabel)
      : selectedOption
        ? optionLabel(selectedOption)
        : '';

  const showClear = !disabled && hasSelection && !open;

  const wrapperWidthClass =
    typeof className === 'string'
      ? className
          .split(/\s+/)
          .filter((token) => /^(w-|min-w-|max-w-|col-)/.test(token))
          .join(' ')
      : '';

  return (
    <div
      className={`relative min-w-0 ${wrapperWidthClass || 'w-full'} ${open ? 'z-[80]' : ''}`}
      ref={containerRef}
      style={open ? { zIndex: 80 } : undefined}
    >
      {label ? (
        <label htmlFor={fieldId} className={labelClassName}>
          {label}
        </label>
      ) : null}

      <div
        className={`relative flex w-full items-center ${open ? selectDropdownOpenClass : ''} rounded-xl`}
      >
        <Search
          className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 shrink-0 text-gray-400"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={fieldId}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fieldId}-listbox`}
          aria-autocomplete="list"
          aria-multiselectable={multiple || undefined}
          autoComplete="off"
          disabled={disabled}
          value={displayValue}
          placeholder={allLabel}
          title={displayValue || allLabel}
          onFocus={openDropdown}
          onClick={openDropdown}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          style={{
            paddingLeft: '2.5rem',
            paddingRight: showClear ? '4rem' : '2.25rem',
          }}
          className={`${filterInputClass} ${
            disabled ? 'cursor-not-allowed opacity-60' : ''
          } ${!multiple && !hasSelection && !open ? 'text-gray-500 font-normal' : ''}`}
        />
        {showClear ? (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-8 top-1/2 z-[1] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Clear filter"
            tabIndex={-1}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <ChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 shrink-0 transition-transform ${
            open ? 'rotate-180 text-blue-500' : 'text-gray-500'
          }`}
          aria-hidden="true"
        />
      </div>

      {open ? (
        <ul
          ref={listRef}
          id={`${fieldId}-listbox`}
          role="listbox"
          className={selectDropdownPanelClass}
        >
          {listOptions.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-gray-400">No matches</li>
          ) : (
            listOptions.map((opt, index) => {
              const isSelected = opt.isAll
                ? selectedValues.length === 0
                : isSelectedValue(multiple ? selectedValues : value, opt.value, multiple);
              const isActive = index === highlight;

              if (multiple) {
                return (
                  <li
                    key={`${String(opt.value)}-${optionLabel(opt)}`}
                    role="option"
                    aria-selected={isSelected}
                    data-option-index={index}
                  >
                    <label
                      className={`${filterCheckboxOptionClass} ${
                        isActive ? 'bg-blue-50/70' : ''
                      } ${opt.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                      onMouseEnter={() => setHighlight(index)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={opt.disabled}
                        onChange={() => selectOption(opt)}
                        className="accent-blue-500 shrink-0"
                      />
                      <span className="min-w-0 leading-5">{optionLabel(opt)}</span>
                    </label>
                  </li>
                );
              }

              return (
                <li
                  key={`${String(opt.value)}-${optionLabel(opt)}`}
                  role="option"
                  aria-selected={isSelected}
                  data-option-index={index}
                >
                  <button
                    type="button"
                    disabled={opt.disabled}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => selectOption(opt)}
                    className={`${selectDropdownOptionClass(isSelected || isActive)} ${
                      isActive && !isSelected ? 'bg-blue-50/70' : ''
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {optionLabel(opt)}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default SearchableFilterSelect;
